/// <amd-module name="Wit/DeliveryPlans/Common/Services/IPlanService" />
import { ArtifactScope, Favorite } from "Favorites/Common/Contracts/Favorite";
import { IFavoritesService } from "Favorites/Common/FavoritesService";
import { getTFSPageData, IProjectData } from "Tfs/Platform/Page";
import { IObservableValue } from "VSS/Core/Observable";
import { IVssObservableService, IVssPageContext, Services, VssObservableService } from "VSS/Platform/Context";
import { IVssContributionService } from "VSS/Platform/Contribution";
import { IdentityRef } from "VSS/Platform/Contracts/WebApi";
import { IVssPageService } from "VSS/Platform/Page";
import { CreatePlan, UpdatePlan } from "Wit/Clients/Work/Contracts/Work";
import { IWorkRestClient } from "Wit/Clients/Work/RestClient/Work.Types";
import { FavoriteTypes } from "Wit/Common/Generated/Constants";
import { PlanData, PlansData } from "Wit/DeliveryPlans/Common/Client/Contracts/DataProvider";
import { reportError } from "Wit/DeliveryPlans/Common/ErrorHandling";

export interface IPlanService extends IVssObservableService<PlanData> {
    createPlan(planToCreate: CreatePlan): Promise<PlanData>;
    deletePlan(id: string): Promise<void>;
    getPlan(id: string): PlanData | undefined;
    getPlans(): PlanData[] | undefined;
    getPlansErrorMessage(): string | undefined;
    favoritePlan(planData: IObservableValue<PlanData | undefined>): Promise<void>;
    unfavoritePlan(planData: IObservableValue<PlanData | undefined>): Promise<void>;
    updatePlan(planId: string, updatePlan: UpdatePlan): Promise<PlanData>;
}

export namespace PlanServiceEvents {
    export const planUpdate = "plan-updated";
}

namespace PlanDataProviders {
    export const PlansDataProvider = "ms.vss-work-web.delivery-plans-data-provider";
}

class PlanService extends VssObservableService<PlanData> implements IPlanService {
    private _currentUser: IdentityRef | undefined;
    private _favoritesService: IFavoritesService;
    private _plans: PlanData[] | undefined;
    private _plansById: { [key: string]: PlanData };
    private _projectData: IProjectData | undefined;

    public _serviceStart(pageContext: IVssPageContext) {
        super._serviceStart(pageContext);
        this._plansById = {};
        this._projectData = getTFSPageData(pageContext)!.project;
        const vssPageService = pageContext.getService<IVssPageService>("IVssPageService");
        const pageData = vssPageService.getData();
        this._currentUser = pageData?.user;
        this._favoritesService = pageContext.getService<IFavoritesService>("IFavoritesService");
    }

    public async createPlan(planToCreate: CreatePlan): Promise<PlanData> {
        const client = this.pageContext.getRestClient<IWorkRestClient>("IWorkRestClient");
        const createdPlan = await client.createPlan(planToCreate, this._projectData!.id);
        const newPlanData = {
            createdByIdentity: createdPlan.createdByIdentity,
            createdDate: createdPlan.createdDate,
            description: createdPlan.description,
            id: createdPlan.id,
            isFavorite: false,
            lastAccessed: createdPlan.modifiedDate,
            modifiedDate: createdPlan.modifiedDate,
            name: createdPlan.name,
            properties: createdPlan.properties,
            revision: createdPlan.revision,
            url: createdPlan.url,
            userPermissions: createdPlan.userPermissions
        };

        return newPlanData;
    }

    public async deletePlan(id: string): Promise<void> {
        const client = this.pageContext.getRestClient<IWorkRestClient>("IWorkRestClient");
        await client.deletePlan(this._projectData!.id, id);
    }

    public getPlan(id: string): PlanData | undefined {
        this._loadPlans();
        return this._plansById[id];
    }

    public getPlans(): PlanData[] | undefined {
        this._loadPlans();
        return this._plans;
    }

    public getPlansErrorMessage(): string | undefined {
        const contributionService = this.pageContext.getService<IVssContributionService>("IVssContributionService");
        const exception = contributionService.getDataProviderExceptions()[PlanDataProviders.PlansDataProvider];
        return exception?.message;
    }

    public async favoritePlan(planData: IObservableValue<PlanData | undefined>): Promise<void> {
        if (planData && planData.value && this._projectData && this._currentUser) {
            const scope: ArtifactScope = { id: this._projectData.id, name: this._projectData.name, type: "Project" };

            const newFavorite = {
                artifactId: planData.value.id,
                artifactName: planData.value.name,
                artifactScope: scope,
                artifactType: FavoriteTypes.WORK_PLAN,
                owner: this._currentUser
            } as Favorite;

            try {
                planData.value.isFavorite = true;
                await this._favoritesService.addFavorite(newFavorite);
            } catch (ex) {
                // set the error and favorite value back
                reportError(this.pageContext, "PlanService.favoritePlan", ex);
                planData.value.isFavorite = false;
            }
        }
    }

    public async unfavoritePlan(planData: IObservableValue<PlanData | undefined>): Promise<void> {
        if (planData && planData.value && this._projectData && this._currentUser) {
            const scope: ArtifactScope = { id: this._projectData.id, name: this._projectData.name, type: "Project" };

            const favorites = await this._favoritesService.getFavorites(FavoriteTypes.WORK_PLAN, scope.type, scope.id, false);
            // we should only match one favorite
            const matchedFavorites = favorites.value.filter(favorite => favorite.artifactId === planData.value!.id);

            matchedFavorites.forEach(async favorite => {
                try {
                    planData.value!.isFavorite = false;
                    await this._favoritesService.removeFavorite(favorite);
                } catch (ex) {
                    // set the error and favorite value back
                    reportError(this.pageContext, "PlanService.unfavoritePlan", ex);
                    planData.value!.isFavorite = true;
                }
            });
        }
    }

    public async updatePlan(planId: string, updatePlan: UpdatePlan): Promise<PlanData> {
        const client = this.pageContext.getRestClient<IWorkRestClient>("IWorkRestClient");
        const updatedPlan = await client.updatePlan(updatePlan, this._projectData!.id, planId);
        const newPlanData = {
            createdByIdentity: updatedPlan.createdByIdentity,
            createdDate: updatedPlan.createdDate,
            description: updatedPlan.description,
            id: updatedPlan.id,
            isFavorite: false,
            lastAccessed: updatedPlan.modifiedDate,
            modifiedDate: updatedPlan.modifiedDate,
            name: updatedPlan.name,
            properties: updatedPlan.properties,
            revision: updatedPlan.revision,
            url: updatedPlan.url,
            userPermissions: updatedPlan.userPermissions
        };

        this._notify(newPlanData, PlanServiceEvents.planUpdate);

        return newPlanData;
    }

    private _loadPlans() {
        const contributionService = this.pageContext.getService<IVssContributionService>("IVssContributionService");
        const plansData = contributionService.getData<PlansData>(PlanDataProviders.PlansDataProvider);
        if (plansData) {
            this._plans = [...plansData.plans];
            this._plans.forEach(plan => {
                this._plansById[plan.id] = plan;
            });
        }
    }
}

const serviceName = "IPlanService";
Services.add(serviceName, { serviceFactory: PlanService });
